import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, humanize, money } from '@/lib/utils';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Select, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { newIdempotencyKey } from '@/lib/utils';
import { LoadMore, useCursorList } from './cursor-list';
import { GoodsReceivedTab } from './GoodsReceivedTab';
import { SUPPLIER_FILTERS, blockedStockNotes, isBelowReorder, supplierActivationBody } from './inventory-view';
import { PrintDocumentButton } from './PrintDocumentButton';

const PRODUCT_PAGE_SIZE = 200;
const SUPPLIER_PAGE_SIZE = 200;
const PURCHASE_ORDER_PAGE_SIZE = 100;

export interface StoreOption {
  id: string;
  name: string;
  branch: { id: string; name: string } | null;
}

/**
 * Store names repeat across sites — every branch has a "Main Pharmacy" — so the
 * branch has to be on the label or the picker is a coin toss in a group.
 */
export function storeLabel(store: StoreOption): string {
  return store.branch ? `${store.branch.name} — ${store.name}` : store.name;
}

export interface ProductRow {
  id: string;
  type: string;
  code: string;
  genericName: string;
  brandName: string | null;
  form: string | null;
  strength: string | null;
  unit: string;
  packSize: number;
  manufacturer: string | null;
  isControlled: boolean;
  isRxRequired: boolean;
  reorderLevel: number;
  taxPct: number;
  taxCategory?: string | null;
  onHand: number;
  quarantinedQty?: number;
  recalledQty?: number;
  nextExpiry?: string | null;
}

interface StockRow {
  id: string;
  product: { id: string; code: string; genericName: string; brandName: string | null; strength: string | null; unit: string; isControlled: boolean };
  store: { id: string; name: string };
  batchNo: string;
  expiryDate: string;
  daysToExpiry: number;
  qtyOnHand: number;
  salePriceMinor: number;
  purchasePriceMinor: number;
  belowReorder: boolean;
  productAvailableQty?: number;
  status?: string;
  statusReason?: string | null;
}

interface ExpiryDashboard {
  windows: { window: number; batches: number; valueMinor: number }[];
  expired: { id: string; product: { code: string; genericName: string }; batchNo: string; expiryDate: string; qtyOnHand: number; valueMinor: number }[];
}

/** Stock, expiry, valuation and goods-in (SRS 7.4, 7.5). */
export function InventoryPage() {
  const { can, hasModule } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => params.get('tab') ?? 'stock');
  const [storeId, setStoreId] = useState('');
  const [search, setSearch] = useState('');
  const [expiryWindow, setExpiryWindow] = useState<number | null>(null);
  const [supplierFilter, setSupplierFilter] = useState('');

  const products = useCursorList<ProductRow>({
    queryKey: ['products', search],
    path: '/pharmacy/products',
    params: { q: search },
    pageSize: PRODUCT_PAGE_SIZE,
    enabled: tab === 'products',
  });

  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
  });

  const stock = useQuery({
    queryKey: ['stock', storeId, search],
    queryFn: () =>
      get<{ data: StockRow[] }>(`/pharmacy/stock${qs({ storeId: storeId || undefined, q: search || undefined, limit: 200 })}`),
    enabled: tab === 'stock',
  });

  const expiry = useQuery({
    queryKey: ['expiry', storeId],
    queryFn: () => get<ExpiryDashboard>(`/pharmacy/stock/expiry${qs({ storeId: storeId || undefined })}`),
    enabled: tab === 'expiry',
  });

  const expiringSoon = useQuery({
    queryKey: ['stock', 'expiring', storeId, expiryWindow],
    queryFn: () =>
      get<StockRow[]>(
        `/pharmacy/stock${qs({ storeId: storeId || undefined, expiringInDays: expiryWindow ?? undefined, limit: 200 })}`,
      ),
    enabled: tab === 'expiry' && expiryWindow !== null,
  });

  const valuation = useQuery({
    queryKey: ['valuation', storeId],
    queryFn: () =>
      get<{ store: string; batches: number; units: number; costValueMinor: number; retailValueMinor: number; marginMinor: number }[]>(
        `/pharmacy/stock/valuation${qs({ storeId: storeId || undefined })}`,
      ),
    enabled: tab === 'valuation',
  });

  const reorder = useQuery({
    queryKey: ['reorder', storeId],
    queryFn: () =>
      get<{ productId: string; code: string; name: string; onHand: number; reorderLevel: number; dailyUse: number; daysOfCover: number | null; suggestedQty: number }[]>(
        `/pharmacy/stock/reorder?storeId=${storeId}`,
      ),
    enabled: tab === 'reorder' && Boolean(storeId),
  });

  const suppliers = useCursorList<SupplierRow>({
    queryKey: ['suppliers', 'list', supplierFilter],
    path: '/pharmacy/suppliers',
    params: { isActive: supplierFilter },
    pageSize: SUPPLIER_PAGE_SIZE,
    enabled: tab === 'suppliers',
  });
  const purchaseOrders = useCursorList<PurchaseOrder>({
    queryKey: ['purchase-orders', 'list'],
    path: '/pharmacy/purchase-orders',
    params: {},
    pageSize: PURCHASE_ORDER_PAGE_SIZE,
    enabled: tab === 'purchase-orders',
  });

  const indents = useQuery({
    queryKey: ['indents'],
    queryFn: () => get<Indent[]>('/pharmacy/indents?limit=100'),
    enabled: tab === 'indents',
  });

  const returnTo = encodeURIComponent(`/inventory?tab=${tab}`);
  const openForm = (path: string, state?: Record<string, unknown>) =>
    navigate(`${path}?returnTo=${returnTo}${storeId ? `&storeId=${storeId}` : ''}`, state ? { state } : undefined);
  const resetSearch = search ? () => setSearch('') : undefined;
  const loadMoreFooter = (list: { hasMore: boolean; loadingMore: boolean; nextPageError: unknown; loadMore: () => void }) =>
    list.hasMore || list.nextPageError ? (
      <div className="w-full">
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} error={list.nextPageError} onLoad={list.loadMore} />
      </div>
    ) : undefined;

  return (
    <>
      <PageHeader
        title={tr('pharmacy', 'inventory')}
        description={tr('pharmacy', 'batches_on_hand_expiry_valuation_and_reordering')}
        actions={
          <>
            <Select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="w-52">
              <option value="">{tr('pharmacy', 'all_stores')}</option>
              {stores.data?.map((store) => (
                <option key={store.id} value={store.id}>
                  {storeLabel(store)}
                </option>
              ))}
            </Select>
            {tab === 'products' && can('product.manage') && (
              <Button onClick={() => openForm('/inventory/products/new')}>{tr('pharmacy', 'add_product')}</Button>
            )}
            {tab === 'purchase-orders' && can('purchase.manage') && (
              <Button onClick={() => openForm('/inventory/purchase-orders/new')}>{tr('pharmacy', 'new_purchase_order')}</Button>
            )}
            {tab === 'suppliers' && can('purchase.manage') && (
              <Button onClick={() => openForm('/inventory/suppliers/new')}>{tr('pharmacy', 'add_supplier')}</Button>
            )}
            {tab === 'indents' && can('indent.request') && <Button onClick={() => openForm('/inventory/indents/new')}>{tr('pharmacy', 'new_indent')}</Button>}
            {tab !== 'products' && tab !== 'purchase-orders' && tab !== 'indents' && tab !== 'suppliers' &&
              can('purchase.manage') && (
              <Button onClick={() => openForm('/inventory/grn/new')}>{tr('pharmacy', 'receive_goods')}</Button>
            )}
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'products', label: tr('pharmacy', 'products') },
          { id: 'stock', label: tr('pharmacy', 'stock') },
          { id: 'expiry', label: tr('pharmacy', 'expiry') },
          { id: 'reorder', label: tr('pharmacy', 'reorder') },
          { id: 'valuation', label: tr('pharmacy', 'valuation') },
          { id: 'suppliers', label: tr('pharmacy', 'suppliers') },
          { id: 'purchase-orders', label: tr('pharmacy', 'purchase_orders') },
          { id: 'goods-received', label: tr('pharmacy', 'goods_received') },
          // Indents are a ward asking the pharmacy for stock. A medical store
          // has no wards, and the tab only ever showed it an empty list.
          ...(hasModule('ipd') ? [{ id: 'indents', label: tr('pharmacy', 'ward_indents') }] : []),
        ]}
      />

      {tab === 'products' && (
        <>
          <SearchFilterBar
            search={{ value: search, onChange: setSearch, placeholder: tr('pharmacy', 'search_by_name_brand_or_code') }}
            onReset={resetSearch}
          />
          <DataTable
            rows={products.rows}
            loading={products.isLoading}
            error={products.firstPageError}
            rowKey={(row) => row.id}
            onRowClick={can('product.manage') ? (row) => openForm(`/inventory/products/${row.id}/edit`, { product: row }) : undefined}
            empty={tr('pharmacy', 'no_products_yet_add_what_you_sell_here_first')}
            footer={loadMoreFooter(products)}
            columns={[
              { header: tr('pharmacy', 'code'), width: '120px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              {
                header: tr('pharmacy', 'product'),
                sort: (row) => row.brandName ?? row.genericName,
                cell: (row) => (
                  <div>
                    <p className="font-medium">{row.brandName ?? row.genericName}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.brandName ? `${row.genericName} · ` : ''}
                      {[row.form, row.strength].filter(Boolean).join(' ') || '—'}
                    </p>
                  </div>
                ),
              },
              { header: tr('pharmacy', 'type'), sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type, 'product_type')}</Badge> },
              { header: tr('pharmacy', 'pack'), align: 'right', sort: (row) => row.packSize, cell: (row) => `${row.packSize} ${row.unit}` },
              {
                header: tr('pharmacy', 'available'),
                align: 'right',
                sort: (row) => row.onHand,
                cell: (row) => (
                  <div>
                    <span className={isBelowReorder(row) ? 'font-semibold text-destructive' : ''}>{row.onHand}</span>
                    {blockedStockNotes(row).map((note) => (
                      <p key={note.label} className="text-xs text-muted-foreground">
                        {note.label} {note.qty}
                      </p>
                    ))}
                  </div>
                ),
              },
              { header: tr('pharmacy', 'reorder_at'), align: 'right', sort: (row) => row.reorderLevel, cell: (row) => row.reorderLevel },
              {
                header: tr('pharmacy', 'flags'),
                cell: (row) => (
                  <div className="flex flex-wrap gap-1">
                    {isBelowReorder(row) && (
                      <Badge variant="destructive" title={tr('pharmacy', 'available_stock_is_at_or_below_the_reorder_level')}>
                        {tr('pharmacy', 'low_stock')}
                      </Badge>
                    )}
                    {row.isRxRequired && <Badge variant="secondary">{tr('pharmacy', 'prescription')}</Badge>}
                    {row.isControlled && <Badge variant="warning">{tr('pharmacy', 'controlled')}</Badge>}
                  </div>
                ),
              },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  can('product.manage') ? (
                    <Button variant="secondary" size="sm" onClick={() => openForm(`/inventory/products/${row.id}/edit`, { product: row })}>
                      {tr('pharmacy', 'edit')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </>
      )}

      {tab === 'stock' && (
        <>
          <SearchFilterBar
            search={{ value: search, onChange: setSearch, placeholder: tr('pharmacy', 'search_by_name_code_or_barcode') }}
            onReset={resetSearch}
          />
          <DataTable
            rows={stock.data?.data}
            loading={stock.isLoading} refreshing={stock.isFetching}
            error={stock.error}
            rowKey={(row) => row.id}
            empty={tr('pharmacy', 'no_stock_matches')}
            columns={[
              {
                header: tr('pharmacy', 'item'),
                sort: (row) => row.product.brandName ?? row.product.genericName,
                cell: (row) => (
                  <div>
                    <p className="font-medium">
                      {row.product.brandName ?? row.product.genericName} {row.product.strength ?? ''}
                      {row.product.isControlled && <Badge variant="destructive" className="ms-1">{tr('pharmacy', 'controlled')}</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {row.product.code} · {row.product.genericName}
                    </p>
                  </div>
                ),
              },
              { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
              {
                header: tr('pharmacy', 'batch'),
                sort: (row) => row.batchNo,
                cell: (row) => (
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="font-mono text-xs">{row.batchNo}</span>
                    {(row.status === 'quarantined' || row.status === 'recalled') && (
                      <Badge variant={row.status === 'recalled' ? 'destructive' : 'warning'} title={row.statusReason ?? undefined}>
                        {humanize(row.status, 'batch_status')}
                      </Badge>
                    )}
                  </div>
                ),
              },
              {
                header: tr('pharmacy', 'expires'),
                sort: (row) => row.expiryDate,
                cell: (row) => (
                  <span className={row.daysToExpiry < 90 ? 'font-medium text-warning' : ''}>
                    {dateOnly(row.expiryDate)}
                    <span className="ms-1 text-xs text-muted-foreground">({row.daysToExpiry} d)</span>
                  </span>
                ),
              },
              {
                header: tr('pharmacy', 'on_hand'),
                align: 'right',
                sort: (row) => row.qtyOnHand,
                cell: (row) => (
                  <span className={row.belowReorder ? 'font-semibold text-destructive' : ''}>
                    {row.qtyOnHand} {row.product.unit}
                  </span>
                ),
              },
              { header: tr('pharmacy', 'cost'), align: 'right', sort: (row) => row.purchasePriceMinor, cell: (row) => money(row.purchasePriceMinor) },
              { header: tr('pharmacy', 'retail'), align: 'right', sort: (row) => row.salePriceMinor, cell: (row) => money(row.salePriceMinor) },
            ]}
          />
        </>
      )}

      {tab === 'expiry' && expiry.data && (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            {expiry.data.windows.map((window) => (
              <button
                key={window.window}
                type="button"
                className="text-start focus-ring rounded-lg"
                onClick={() => setExpiryWindow(expiryWindow === window.window ? null : window.window)}
                aria-pressed={expiryWindow === window.window}
              >
                <StatTile
                  label={tr('pharmacy', 'expiring_in_x_days', { window: window.window })}
                  value={window.batches}
                  sub={
                    expiryWindow === window.window
                      ? tr('pharmacy', 'showing_these_click_again_to_hide')
                      : tr('pharmacy', 'x_at_cost_click_to_list', { valueMinor: money(window.valueMinor) })
                  }
                  tone={window.window === 30 && window.batches > 0 ? 'destructive' : 'warning'}
                />
              </button>
            ))}
          </div>
          {expiryWindow !== null && (
            <div className="mb-6">
              <h2 className="mb-2 text-sm font-semibold">{tr('pharmacy', 'expiring_within_x_days', { expiryWindow: expiryWindow })}</h2>
              <DataTable
                rows={expiringSoon.data}
                loading={expiringSoon.isLoading} refreshing={expiringSoon.isFetching}
                error={expiringSoon.error}
                rowKey={(row) => row.id}
                empty={tr('pharmacy', 'nothing_falls_in_that_window')}
                columns={[
                  { header: tr('pharmacy', 'item'), sort: (row) => row.product.genericName, cell: (row) => `${row.product.code} — ${row.product.genericName}` },
                  { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
                  { header: tr('pharmacy', 'batch'), sort: (row) => row.batchNo, cell: (row) => <span className="font-mono text-xs">{row.batchNo}</span> },
                  { header: tr('pharmacy', 'expires'), sort: (row) => row.expiryDate, cell: (row) => dateOnly(row.expiryDate) },
                  {
                    header: tr('pharmacy', 'days_left'),
                    align: 'right',
                    sort: (row) => row.daysToExpiry,
                    cell: (row) => (
                      <span className={row.daysToExpiry <= 30 ? 'font-semibold text-destructive' : ''}>
                        {row.daysToExpiry}
                      </span>
                    ),
                  },
                  { header: tr('pharmacy', 'qty_2'), align: 'right', sort: (row) => row.qtyOnHand, cell: (row) => row.qtyOnHand },
                ]}
              />
            </div>
          )}

          <h2 className="mb-2 text-sm font-semibold">{tr('pharmacy', 'already_expired')}</h2>
          <DataTable
            rows={expiry.data.expired}
            rowKey={(row) => row.id}
            empty={tr('pharmacy', 'nothing_has_expired_the_fefo_picking_is_doing')}
            columns={[
              { header: tr('pharmacy', 'item'), sort: (row) => row.product.genericName, cell: (row) => `${row.product.code} — ${row.product.genericName}` },
              { header: tr('pharmacy', 'batch'), sort: (row) => row.batchNo, cell: (row) => <span className="font-mono text-xs">{row.batchNo}</span> },
              { header: tr('pharmacy', 'expired'), sort: (row) => row.expiryDate, cell: (row) => dateOnly(row.expiryDate) },
              { header: tr('pharmacy', 'qty_2'), align: 'right', sort: (row) => row.qtyOnHand, cell: (row) => row.qtyOnHand },
              { header: tr('pharmacy', 'loss_at_cost'), align: 'right', sort: (row) => row.valueMinor, cell: (row) => money(row.valueMinor) },
            ]}
          />
        </>
      )}

      {tab === 'reorder' &&
        (storeId ? (
          <DataTable
            rows={reorder.data}
            loading={reorder.isLoading} refreshing={reorder.isFetching}
            error={reorder.error}
            rowKey={(row) => row.productId}
            empty={tr('pharmacy', 'nothing_is_below_its_reorder_level')}
            columns={[
              { header: tr('pharmacy', 'item'), sort: (row) => row.name, cell: (row) => `${row.code} — ${row.name}` },
              { header: tr('pharmacy', 'on_hand'), align: 'right', sort: (row) => row.onHand, cell: (row) => row.onHand },
              { header: tr('pharmacy', 'reorder_at'), align: 'right', sort: (row) => row.reorderLevel, cell: (row) => row.reorderLevel },
              { header: tr('pharmacy', 'daily_use'), align: 'right', sort: (row) => row.dailyUse, cell: (row) => row.dailyUse },
              {
                header: tr('pharmacy', 'cover'),
                align: 'right',
                sort: (row) => row.daysOfCover,
                cell: (row) =>
                  row.daysOfCover === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className={row.daysOfCover < 7 ? 'font-semibold text-destructive' : ''}>
                      {row.daysOfCover} d
                    </span>
                  ),
              },
              { header: tr('pharmacy', 'suggested_order'), align: 'right', sort: (row) => row.suggestedQty, cell: (row) => <strong>{row.suggestedQty}</strong> },
            ]}
          />
        ) : (
          <Alert>{tr('pharmacy', 'choose_a_store_to_see_its_reorder_suggestions')}</Alert>
        ))}

      {tab === 'valuation' && (
        <DataTable
          rows={valuation.data}
          loading={valuation.isLoading} refreshing={valuation.isFetching}
          error={valuation.error}
          rowKey={(row) => row.store}
          columns={[
            { header: tr('pharmacy', 'store'), sort: (row) => row.store, cell: (row) => row.store },
            { header: tr('pharmacy', 'batches'), align: 'right', sort: (row) => row.batches, cell: (row) => row.batches },
            { header: tr('pharmacy', 'units'), align: 'right', sort: (row) => row.units, cell: (row) => row.units },
            { header: tr('pharmacy', 'at_cost_2'), align: 'right', sort: (row) => row.costValueMinor, cell: (row) => money(row.costValueMinor) },
            { header: tr('pharmacy', 'at_retail'), align: 'right', sort: (row) => row.retailValueMinor, cell: (row) => money(row.retailValueMinor) },
            { header: tr('pharmacy', 'margin'), align: 'right', sort: (row) => row.marginMinor, cell: (row) => <strong className="text-success">{money(row.marginMinor)}</strong> },
          ]}
        />
      )}

      {tab === 'suppliers' && (
        <>
          <SearchFilterBar
            filters={
              <FilterField label={tr('pharmacy', 'status')}>
                <Select
                  className="w-48"
                  aria-label={tr('pharmacy', 'filter_suppliers_by_status')}
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                >
                  {SUPPLIER_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FilterField>
            }
            onReset={supplierFilter ? () => setSupplierFilter('') : undefined}
          />
          <DataTable
            rows={suppliers.rows}
            loading={suppliers.isLoading}
            error={suppliers.firstPageError}
            rowKey={(row) => row.id}
            onRowClick={can('purchase.manage') ? (row) => openForm(`/inventory/suppliers/${row.id}/edit`, { supplier: row }) : undefined}
            empty={
              supplierFilter
                ? tr('pharmacy', 'no_suppliers_match_that_filter')
                : tr('pharmacy', 'no_suppliers_yet_add_the_people_you_buy_from')
            }
            footer={loadMoreFooter(suppliers)}
            columns={[
              { header: tr('pharmacy', 'code'), width: '120px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              { header: tr('pharmacy', 'supplier'), sort: (row) => row.name, cell: (row) => row.name },
              { header: tr('pharmacy', 'contact'), sort: (row) => row.contactPerson, cell: (row) => [row.contactPerson, row.phone].filter(Boolean).join(' · ') || '—' },
              { header: tr('pharmacy', 'payment_terms'), align: 'right', sort: (row) => row.paymentTermsDays, cell: (row) => `${row.paymentTermsDays} days` },
              {
                header: tr('pharmacy', 'status'),
                sort: (row) => row.isActive,
                cell: (row) => (
                  <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('pharmacy', 'active') : tr('pharmacy', 'inactive')}</Badge>
                ),
              },
              {
                header: '',
                align: 'right',
                cell: (row) => (
                  <div className="flex items-center justify-end gap-1">
                    {can('purchase.manage') && <SupplierActivationButton supplier={row} />}
                    {can('supplier.payment.create', 'purchase.manage') && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/payables?tab=statement&supplier=${row.id}`);
                        }}
                      >
                        {tr('pharmacy', 'statement')}
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}

      {tab === 'purchase-orders' && (
        <DataTable
          rows={purchaseOrders.rows}
          loading={purchaseOrders.isLoading}
          error={purchaseOrders.firstPageError}
          rowKey={(row) => row.id}
          empty={tr('pharmacy', 'no_purchase_orders_raised_yet')}
          footer={loadMoreFooter(purchaseOrders)}
          columns={[
            { header: tr('pharmacy', 'number'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
            { header: tr('pharmacy', 'supplier'), sort: (row) => row.supplier.name, cell: (row) => row.supplier.name },
            { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
            { header: tr('pharmacy', 'lines'), align: 'right', sort: (row) => row.items.length, cell: (row) => row.items.length },
            { header: tr('pharmacy', 'total'), align: 'right', sort: (row) => row.totalMinor, cell: (row) => money(row.totalMinor) },
            {
              header: tr('pharmacy', 'status'),
              sort: (row) => row.status,
              cell: (row) => (
                <div>
                  <StatusBadge status={row.status} />
                  {row.closeReason && <p className="mt-0.5 text-xs text-muted-foreground">{row.closeReason}</p>}
                </div>
              ),
            },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <div className="flex items-center justify-end gap-1">
                  <PrintDocumentButton path={`/pharmacy/print/purchase-order/${row.id}`} label={tr('pharmacy', 'print_purchase_order')} compact />
                  <PurchaseOrderActions order={row} />
                </div>
              ),
            },
          ]}
        />
      )}

      {tab === 'goods-received' && <GoodsReceivedTab storeId={storeId} />}

      {tab === 'indents' && (
        <DataTable
          rows={indents.data}
          loading={indents.isLoading} refreshing={indents.isFetching}
          error={indents.error}
          rowKey={(row) => row.id}
          empty={tr('pharmacy', 'no_ward_indents_yet')}
          columns={[
            { header: tr('pharmacy', 'number'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
            {
              header: tr('pharmacy', 'for'),
              sort: (row) => row.admission?.patient.fullName ?? row.ward?.name,
              cell: (row) =>
                row.admission ? `${row.admission.patient.fullName} · ${row.admission.number}` : (row.ward?.name ?? '—'),
            },
            { header: tr('pharmacy', 'from_store'), sort: (row) => row.fromStore.name, cell: (row) => row.fromStore.name },
            { header: tr('pharmacy', 'lines'), align: 'right', sort: (row) => row.items.length, cell: (row) => row.items.length },
            { header: tr('pharmacy', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
            {
              header: '',
              align: 'right',
              cell: (row) => <IndentActions indent={row} />,
            },
          ]}
        />
      )}

    </>
  );
}

export interface SupplierRow {
  id: string;
  code: string;
  name: string;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  taxNumber: string | null;
  paymentTermsDays: number;
  isActive: boolean;
}
export interface PurchaseOrder {
  id: string;
  number: string;
  status: string;
  closeReason?: string | null;
  totalMinor: number;
  supplier: { id: string; name: string };
  store: { id: string; name: string };
  items: {
    id: string;
    productId: string;
    qty: number;
    receivedQty: number;
    unitCostMinor: number;
    product: { code: string; genericName: string; unit: string };
  }[];
}

function SupplierActivationButton({ supplier }: { supplier: SupplierRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const change = useMutation({
    mutationFn: () => post('/pharmacy/suppliers', supplierActivationBody(supplier, !supplier.isActive)),
    onSuccess: () => {
      toast.success(
        supplier.isActive ? tr('pharmacy', 'supplier_deactivated') : tr('pharmacy', 'supplier_reactivated'),
        supplier.isActive
          ? tr('pharmacy', 'no_new_purchase_order_or_goods_received_note')
          : tr('pharmacy', 'they_can_be_ordered_from_and_received_from_again'),
      );
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (caught) => toast.error(tr('pharmacy', 'could_not_change_the_supplier'), describeError(caught)),
  });

  return (
    <Button
      size="sm"
      variant="ghost"
      loading={change.isPending}
      onClick={(event) => {
        event.stopPropagation();
        change.mutate();
      }}
    >
      {supplier.isActive ? tr('pharmacy', 'deactivate') : tr('pharmacy', 'reactivate')}
    </Button>
  );
}

interface Indent {
  id: string;
  number: string;
  status: string;
  notes: string | null;
  fromStore: { id: string; name: string };
  ward: { id: string; name: string } | null;
  admission: { id: string; number: string; patient: { id: string; mrn: string; fullName: string } } | null;
  items: { id: string; qty: number; issuedQty: number; product: { id: string; code: string; genericName: string; unit: string } }[];
}

/** Approve / reject / issue, whichever this indent's status still allows. */
function IndentActions({ indent }: { indent: Indent }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['indents'] });

  const approve = useMutation({
    mutationFn: () => post(`/pharmacy/indents/${indent.id}/approve`),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'indent_approved'));
      invalidate();
    },
    onError: (error) => toast.error(tr('pharmacy', 'could_not_approve'), describeError(error)),
  });

  const reject = useMutation({
    mutationFn: () => post(`/pharmacy/indents/${indent.id}/reject`, { reason }),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'indent_rejected'));
      setRejecting(false);
      setReason('');
      invalidate();
    },
    onError: (error) => toast.error(tr('pharmacy', 'could_not_reject'), describeError(error)),
  });

  const issue = useMutation({
    mutationFn: () =>
      post(
        `/pharmacy/indents/${indent.id}/issue`,
        {
          lines: indent.items
            .filter((i) => i.qty - i.issuedQty > 0)
            .map((i) => ({ indentItemId: i.id, qty: i.qty - i.issuedQty })),
          chargeToAdmission: Boolean(indent.admission),
        },
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'issued_to_the_ward'), indent.admission ? tr('pharmacy', 'posted_to_the_patient_bill') : undefined);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: (error) => toast.error(tr('pharmacy', 'could_not_issue'), describeError(error)),
  });

  if (indent.status === 'requested' && can('indent.issue')) {
    return (
      <>
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setRejecting(true)}>
            {tr('pharmacy', 'reject')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => approve.mutate()} loading={approve.isPending}>
            {tr('pharmacy', 'approve')}
          </Button>
        </div>
        <Dialog
          open={rejecting}
          onClose={() => setRejecting(false)}
          title={tr('pharmacy', 'reject_this_indent')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRejecting(false)}>
                {tr('pharmacy', 'cancel')}
              </Button>
              <Button variant="destructive" onClick={() => reject.mutate()} loading={reject.isPending} disabled={!reason.trim()}>
                {tr('pharmacy', 'reject')}
              </Button>
            </>
          }
        >
          <Field label={tr('pharmacy', 'reason')} required>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Dialog>
      </>
    );
  }

  if ((indent.status === 'approved' || indent.status === 'partially_issued') && can('indent.issue')) {
    return (
      <Button size="sm" onClick={() => issue.mutate()} loading={issue.isPending}>
        {tr('pharmacy', 'issue')}
      </Button>
    );
  }

  return null;
}

type OrderEnding = 'cancel' | 'close';

function endingFor(status: string): OrderEnding | null {
  if (status === 'draft' || status === 'submitted') return 'cancel';
  if (status === 'partially_received') return 'close';
  return null;
}

function PurchaseOrderActions({ order }: { order: PurchaseOrder }) {
  const { can } = useAuth();
  const [asking, setAsking] = useState(false);
  const ending = endingFor(order.status);

  if (!ending || !can('purchase.manage')) return null;

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setAsking(true)}>
        {ending === 'cancel' ? tr('pharmacy', 'cancel') : tr('pharmacy', 'close_short')}
      </Button>
      {asking && <PurchaseOrderReasonDialog order={order} ending={ending} onClose={() => setAsking(false)} />}
    </>
  );
}

function PurchaseOrderReasonDialog({
  order,
  ending,
  onClose,
}: {
  order: PurchaseOrder;
  ending: OrderEnding;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancelling = ending === 'cancel';

  const submit = useMutation({
    mutationFn: () => {
      const body = { reason: reason.trim() };
      return cancelling
        ? post(`/pharmacy/purchase-orders/${order.id}/cancel`, body)
        : post(`/pharmacy/purchase-orders/${order.id}/close`, body);
    },
    onSuccess: () => {
      toast.success(cancelling ? tr('pharmacy', 'purchase_order_cancelled') : tr('pharmacy', 'purchase_order_closed_short'), order.number);
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={cancelling ? tr('pharmacy', 'cancel_x', { number: order.number }) : tr('pharmacy', 'close_x_short', { number: order.number })}
      description={
        cancelling
          ? tr('pharmacy', 'nothing_has_been_received_against_this_order_it')
          : tr('pharmacy', 'what_has_already_been_received_stays_in_stock')
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
            {tr('pharmacy', 'keep_order')}
          </Button>
          <Button
            variant="destructive"
            loading={submit.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => {
              setError(null);
              submit.mutate();
            }}
          >
            {cancelling ? tr('pharmacy', 'cancel_order') : tr('pharmacy', 'close_short')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('pharmacy', 'reason')} required hint={tr('pharmacy', 'at_least_3_characters_kept_with_the_order')}>
          <Textarea rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

